"use client";

import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { Select } from "@package/subframe/components/Select";
import { TextArea } from "@package/subframe/components/TextArea";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherX } from "@subframe/core";
import { useEffect, useState } from "react";
import { z } from "zod";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useListRoomsQueryControllerList } from "@/generated/rooms/rooms";

import type { RegisteredUnit } from "./RegisteredUnitTable";

/**
 * Add/edit form for a registered unit. Presentational: the caller owns the
 * mutations, so this file stays free of per-unit-type generated hooks.
 */
/** Radix Select cannot hold an empty value, so "no room" needs a stand-in. */
const NO_ROOM = "none";

const unitSchema = (roomRequired: boolean) =>
  z.object({
    name: z
      .string()
      .trim()
      .min(1, "Name is required")
      .max(100, "Name must be less than 100 characters"),
    roomId: roomRequired
      ? z.string().uuid("Room is required")
      : z.string().uuid().optional().or(z.literal("")),
    notes: z.string().trim().max(500).optional(),
  });

export interface RegisteredUnitFormValues {
  name: string;
  roomId?: string;
  notes?: string;
  isActive: boolean;
}

export interface RegisteredUnitFormCopy {
  /** Noun used across the dialog, e.g. "Emergency Light". */
  unitLabel: string;
  /** Example shown in the name field. */
  namePlaceholder: string;
  /** Example shown in the notes field. */
  notesPlaceholder: string;
  /** Shown when the nursery has no active rooms to attach the unit to. */
  noRoomsHelpText: string;
  /** False for unit types that may be registered without a room. */
  roomRequired?: boolean;
}

interface RegisteredUnitFormDialogProps {
  nurseryId: string;
  unit?: RegisteredUnit | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: RegisteredUnitFormValues) => void;
  isPending: boolean;
  copy: RegisteredUnitFormCopy;
}

export function RegisteredUnitFormDialog({
  nurseryId,
  unit,
  open,
  onOpenChange,
  onSubmit,
  isPending,
  copy,
}: RegisteredUnitFormDialogProps) {
  const mode = unit ? "edit" : "create";

  const [name, setName] = useState("");
  const [roomId, setRoomId] = useState("");
  const [notes, setNotes] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (unit && mode === "edit") {
      setName(unit.name);
      setRoomId(unit.room?.id ?? "");
      setNotes(unit.notes ?? "");
      setIsActive(unit.isActive);
    }
  }, [unit, mode]);

  useEffect(() => {
    if (!open || mode === "create") {
      setName("");
      setRoomId("");
      setNotes("");
      setIsActive(true);
      setErrors({});
    }
  }, [open, mode]);

  const { data: roomsData, isLoading: isLoadingRooms } =
    useListRoomsQueryControllerList(
      nurseryId,
      { includeInactive: true },
      { query: { enabled: open, refetchOnMount: "always" } },
    );
  const activeRooms = (roomsData?.data ?? []).filter((r) => r.isActive);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const parsed = unitSchema(copy.roomRequired ?? true).safeParse({
      name,
      roomId,
      notes: notes.trim() || undefined,
    });

    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      parsed.error.issues.forEach((err) => {
        fieldErrors[err.path.join(".")] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    onSubmit({
      ...parsed.data,
      roomId: parsed.data.roomId || undefined,
      isActive,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-w-md max-h-[90vh] overflow-y-auto p-0"
      >
        <DialogTitle className="sr-only">
          {mode === "edit" ? `Edit ${copy.unitLabel}` : `Add ${copy.unitLabel}`}
        </DialogTitle>

        <form onSubmit={handleSubmit} className="flex flex-col">
          <div className="flex items-center justify-between border-b border-solid border-secondary-beige-200 px-6 py-4">
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                {mode === "edit"
                  ? `Edit ${copy.unitLabel}`
                  : `Add ${copy.unitLabel}`}
              </span>
              <span className="text-body font-body text-subtext-color">
                {mode === "edit"
                  ? `Update this ${copy.unitLabel.toLowerCase()}'s details.`
                  : `Register a new ${copy.unitLabel.toLowerCase()} for this nursery.`}
              </span>
            </div>
            <IconButton
              variant="neutral-tertiary"
              size="medium"
              icon={<FeatherX />}
              onClick={() => onOpenChange(false)}
            />
          </div>

          <div className="flex flex-col gap-6 px-6 py-6">
            <TextField
              className="h-auto w-full"
              size="large"
              label="Name / number"
              error={!!errors.name}
              helpText={errors.name}
            >
              <TextField.Input
                placeholder={copy.namePlaceholder}
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isPending}
              />
            </TextField>

            <Select
              label={copy.roomRequired === false ? "Room (optional)" : "Room"}
              placeholder={
                isLoadingRooms
                  ? "Loading rooms…"
                  : activeRooms.length === 0
                    ? "No active rooms available"
                    : "Select a room"
              }
              value={roomId || undefined}
              onValueChange={(v) => setRoomId(v === NO_ROOM ? "" : v)}
              disabled={isPending || isLoadingRooms || activeRooms.length === 0}
              error={!!errors.roomId}
              helpText={
                activeRooms.length === 0 &&
                !isLoadingRooms &&
                copy.roomRequired !== false
                  ? copy.noRoomsHelpText
                  : errors.roomId
              }
            >
              {copy.roomRequired === false && roomId ? (
                <Select.Item value={NO_ROOM}>No room</Select.Item>
              ) : null}
              {activeRooms.map((room) => (
                <Select.Item key={room.id} value={room.id}>
                  {room.name}
                </Select.Item>
              ))}
            </Select>

            <TextArea
              className="h-auto w-full"
              label="Notes (optional)"
              error={!!errors.notes}
              helpText={errors.notes}
            >
              <TextArea.Input
                placeholder={copy.notesPlaceholder}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isPending}
              />
            </TextArea>

            <Select
              label="Status"
              value={isActive ? "active" : "inactive"}
              onValueChange={(v) => setIsActive(v === "active")}
              disabled={isPending}
            >
              <Select.Item value="active">Active</Select.Item>
              <Select.Item value="inactive">Inactive</Select.Item>
            </Select>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-solid border-secondary-beige-200 px-6 py-4">
            <Button
              type="button"
              variant="neutral-primary"
              size="large"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="brand-primary"
              size="large"
              disabled={isPending}
            >
              {isPending
                ? mode === "edit"
                  ? "Saving..."
                  : "Adding..."
                : mode === "edit"
                  ? "Save Changes"
                  : `Add ${copy.unitLabel}`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
